/**
 * Agrupación de marcadores por distancia EN PANTALLA.
 *
 * El solape de marcadores ocurre en píxeles, no en metros: dos puntos a 1 km
 * se pisan a zoom 10 y se separan a zoom 14. Por eso el umbral se deriva del
 * zoom en vez de ser una constante geográfica — la lección del
 * `spreadOverlappingMarkers` que se retiró, que repartía con un radio fijo de
 * ~3 m (0,06 px a la escala de un recorrido) y por tanto no separaba nada.
 *
 * Se implementa a mano en vez de tirar de `@googlemaps/js-markerclusterer`:
 * esa librería está pensada para 10.000+ puntos (aquí hay ≤ 50), posee el
 * ciclo de vida de los marcadores (chocaría con el `<Marker>` de React de
 * `Map/_internal`), habría que escribirle el renderer igual, y necesitamos que
 * pulsar SELECCIONE un tramo y no solo que acerque. Se conserva su parámetro
 * de referencia: radio 60 px.
 *
 * Ver `docs/superpowers/specs/2026-08-10-rv-mapa-agrupacion-por-zoom-design.md`.
 */

export interface ClusterCoordinates {
  lat: number;
  lng: number;
}

/** Radio por defecto — el mismo que trae `@googlemaps/js-markerclusterer`. */
export const CLUSTER_RADIUS_PX = 60;

/**
 * Metros por píxel en Web Mercator: `156543,03392 · cos(lat) / 2^zoom`.
 * Es la constante estándar (circunferencia ecuatorial / 256 px del tile base).
 */
export const metersPerPixel = (lat: number, zoom: number): number =>
  (156543.03392 * Math.cos((lat * Math.PI) / 180)) / Math.pow(2, zoom);

/**
 * Distancia aproximada en metros entre dos coordenadas cercanas
 * (equirrectangular — a las distancias que nos ocupan, decenas de km como
 * mucho, el error frente a Haversine es despreciable y es mucho más barata).
 */
export const metersBetween = (
  a: ClusterCoordinates,
  b: ClusterCoordinates
): number => {
  const latMean = ((a.lat + b.lat) / 2) * (Math.PI / 180);
  const dLat = (b.lat - a.lat) * 111320;
  const dLng = (b.lng - a.lng) * 111320 * Math.cos(latMean);
  return Math.hypot(dLat, dLng);
};

/**
 * Agrupa por cercanía en pantalla al zoom dado.
 *
 * Recorrido codicioso en el orden en que llegan los elementos (para el RV, el
 * cronológico), agregando cada uno al primer grupo cuyo ANCLA —su primer
 * miembro— esté dentro del radio. Deliberadamente NO es una rejilla: la
 * agrupación depende solo de las posiciones y del zoom, nunca de dónde esté el
 * viewport, así que **panear no recompone los grupos**. Es el parpadeo que la
 * documentación de Google le reprocha a su propio `GridAlgorithm`.
 *
 * Los elementos que comparten coordenada exacta caen siempre en el mismo grupo
 * (distancia 0 ≤ cualquier radio), a cualquier zoom.
 */
export const clusterByScreenDistance = <T extends { position: ClusterCoordinates }>(
  items: T[],
  zoom: number,
  radiusPx: number = CLUSTER_RADIUS_PX
): T[][] => {
  if (items.length === 0) return [];
  const referenceLat = items[0].position.lat;
  const thresholdMeters = radiusPx * metersPerPixel(referenceLat, zoom);
  const clusters: T[][] = [];
  const anchors: ClusterCoordinates[] = [];
  items.forEach((item) => {
    const index = anchors.findIndex(
      (anchor) => metersBetween(anchor, item.position) <= thresholdMeters
    );
    if (index >= 0) {
      clusters[index].push(item);
    } else {
      clusters.push([item]);
      anchors.push(item.position);
    }
  });
  return clusters;
};

// ═══════════════════════════════════════════════════════════════════
// SPIDERFY — abrir en abanico lo que el zoom NO puede separar
//
// Cuando varios tramos acaban en la coordenada IDÉNTICA, ningún zoom los
// separa: el contador dice «14» y no hay forma de llegar a esos 14. La
// respuesta del sector para exactamente este caso es el spiderfy, y es el
// comportamiento POR DEFECTO de `Leaflet.markercluster`
// (`spiderfyOnMaxZoom`), que además lo dispara al zoom actual —sin esperar al
// máximo— precisamente cuando todos los puntos comparten lat/lng.
//
// Geometría tomada de `OverlappingMarkerSpiderfier` (la implementación de
// referencia): círculo hasta 8 puntos, espiral a partir de 9
// (`circleSpiralSwitchover`). Se DESVÍA en una cosa a propósito: sus
// separaciones son de 23 px (círculo) y 26 px (espiral), pensadas para ratón;
// aquí el consumidor es una tablet en carretera. Se suben a 48 px, que es lo
// que hace falta para que las áreas táctiles de 44 px (`TOUCH_TARGET_SIZE_PX`
// en `Map/_internal`, el mínimo de Apple/Material) NO se solapen entre
// marcadores vecinos — si se solapasen, el toque volvería a ser ambiguo, que
// es el problema del que venimos.
// ═══════════════════════════════════════════════════════════════════

/** A partir de este número de puntos, espiral en vez de círculo (OMS: 9). */
export const CIRCLE_SPIRAL_SWITCHOVER = 9;
const CIRCLE_FOOT_SEPARATION_PX = 48;
const CIRCLE_START_ANGLE = Math.PI / 6;
const SPIRAL_FOOT_SEPARATION_PX = 48;
const SPIRAL_LENGTH_START_PX = 22;
// 12 y no el 4 de OMS: con 4-5 las vueltas adyacentes de la espiral quedan a
// ~35 px, así que las áreas táctiles de 44 px de dos marcadores de VUELTAS
// distintas se solapaban aunque los consecutivos estuviesen bien separados.
// Medido: con 12 la separación mínima real sube a 45 px (test que lo vigila).
const SPIRAL_LENGTH_FACTOR = 12;

export interface PixelOffset {
  x: number;
  y: number;
}

/**
 * Posiciones (en píxeles, relativas al punto real) en las que se despliegan
 * `count` marcadores coincidentes. Círculo hasta 8, espiral por encima.
 */
export const spiderfyPixelOffsets = (count: number): PixelOffset[] => {
  if (count <= 1) return [{ x: 0, y: 0 }];
  if (count < CIRCLE_SPIRAL_SWITCHOVER) {
    const legLength =
      (CIRCLE_FOOT_SEPARATION_PX * (2 + count)) / (2 * Math.PI);
    const step = (2 * Math.PI) / count;
    return Array.from({ length: count }, (_, i) => {
      const angle = CIRCLE_START_ANGLE + i * step;
      return {
        x: legLength * Math.cos(angle),
        y: legLength * Math.sin(angle),
      };
    });
  }
  // Espiral de Arquímedes, misma progresión que OMS.
  let legLength = SPIRAL_LENGTH_START_PX;
  let angle = 0;
  return Array.from({ length: count }, () => {
    angle += SPIRAL_FOOT_SEPARATION_PX / legLength;
    const point = {
      x: legLength * Math.cos(angle),
      y: legLength * Math.sin(angle),
    };
    legLength += (2 * Math.PI * SPIRAL_LENGTH_FACTOR) / angle;
    return point;
  });
};

/**
 * Traslada los desplazamientos en píxeles a coordenadas reales alrededor del
 * ancla, al zoom dado. `y` crece hacia ABAJO en pantalla y la latitud crece
 * hacia arriba, de ahí el signo.
 */
export const spiderfyPositions = (
  anchor: ClusterCoordinates,
  count: number,
  zoom: number
): ClusterCoordinates[] => {
  const mpp = metersPerPixel(anchor.lat, zoom);
  const cosLat = Math.cos((anchor.lat * Math.PI) / 180);
  return spiderfyPixelOffsets(count).map(({ x, y }) => ({
    lat: anchor.lat - (y * mpp) / 111320,
    lng: anchor.lng + (x * mpp) / (111320 * cosLat),
  }));
};
